import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ownerDisplayName } from "@/lib/ownerDisplay";
import NewPropertyForm from "@/components/NewPropertyForm";
import AiPropertyCreate from "./AiPropertyCreate";

/**
 * Stage "property": scelta immobile esistente o creazione inline.
 *
 * Iter209dz — Il form di creazione inline usa ora `NewPropertyForm`
 * (componente condiviso con /properties) per evitare divergenze fra
 * i due flussi di creazione immobile.
 */
export default function PropertyStage({
  selected,
  properties,
  form,
  propSearch,
  createMode,
  newPropForm,
  creatingProp,
  onPatchForm,
  onSetPropSearch,
  onSetCreateMode,
  onPatchNewPropForm,
  onSubmitNewProperty,
  onNext,
  onBack,
  clientsProId = null,
}) {
  // Iter209es — Biforcazione iniziale: "manual" (flusso attuale) | "ai" | null (scelta).
  const [mode, setMode] = useState(form.property_id ? "existing" : null);
  useEffect(() => {
    if (form.property_id) setMode("existing");
  }, [form.property_id]);
  const goChoice = () => { onSetCreateMode(false); setMode(null); };
  const selectedProp = properties.find((p) => p._id === form.property_id);
  const q = propSearch.trim().toLowerCase();
  const norm = (s) => (s || "").toString().toLowerCase();
  const filtered = !q
    ? properties.slice(0, 12)
    : properties
        .filter((p) => {
          const hay = [p.address, p.city, p.province, p.zip_code, p.owner_first_name, p.owner_last_name, p.owner_company_name]
            .map(norm)
            .join(" ");
          return q.split(/\s+/).every((tok) => hay.includes(tok));
        })
        .slice(0, 12);

  if (mode === null) {
    return (
      <div className="space-y-4 py-2" data-testid="property-mode-choice">
        <p className="text-sm text-muted-foreground">Come vuoi indicare l'immobile per "{selected.name}"?</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button type="button" onClick={() => { onSetCreateMode(false); setMode("existing"); }}
            className="p-4 rounded-lg border-2 text-left hover:border-purple-500 hover:bg-purple-50 transition-colors"
            data-testid="property-mode-existing">
            <div className="font-semibold">🏠 Immobile esistente</div>
            <div className="text-xs text-muted-foreground mt-1">Seleziona un immobile già presente in archivio</div>
          </button>
          <button type="button" onClick={() => { onSetCreateMode(true); setMode("manual"); }}
            className="p-4 rounded-lg border-2 text-left hover:border-purple-500 hover:bg-purple-50 transition-colors"
            data-testid="property-mode-manual">
            <div className="font-semibold">📝 Creazione manuale</div>
            <div className="text-xs text-muted-foreground mt-1">Compila i dati di un nuovo immobile</div>
          </button>
          <button type="button" onClick={() => setMode("ai")}
            className="p-4 rounded-lg border-2 text-left hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
            data-testid="property-mode-ai">
            <div className="font-semibold">🤖 Creazione con AI</div>
            <div className="text-xs text-muted-foreground mt-1">Carica i documenti, l'AI compila i dati per te</div>
          </button>
        </div>
        {onBack && (
          <button type="button" onClick={onBack} className="text-xs text-muted-foreground hover:text-foreground" data-testid="property-back-btn">
            ← Indietro
          </button>
        )}
      </div>
    );
  }

  if (mode === "ai") {
    return (
      <div className="py-2">
        <AiPropertyCreate
          submitting={creatingProp}
          onCancel={() => setMode(null)}
          onConfirm={(vals, files) => { onPatchNewPropForm(vals); onSubmitNewProperty(vals, files); }}
        />
      </div>
    );
  }

  if (mode === "manual") {
    return (
      <div className="space-y-4 py-2" data-testid="property-mode-manual-form">
        <div className="flex items-center justify-between">
          <div className="font-semibold text-purple-800">Nuovo immobile</div>
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={goChoice}
            data-testid="mp-cancel-new-property"
          >
            ← Indietro
          </button>
        </div>
        <NewPropertyForm
          value={newPropForm}
          onChange={onPatchNewPropForm}
          onSubmit={onSubmitNewProperty}
          submitting={creatingProp}
          submitLabel="Crea e seleziona"
          testidPrefix="mp-new-prop"
          showNotes={false}
          clientPicker={true}
          clientsProId={clientsProId}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 py-2" data-testid="property-mode-existing-list">
      <p className="text-sm text-muted-foreground">Seleziona l'immobile a cui associare "{selected.name}".</p>
          <Label>Immobile *</Label>
          <div className="mt-1">
            {selectedProp && (
              <div
                className="flex items-start gap-3 p-3 border-2 border-purple-400 rounded-md bg-purple-50/60"
                data-testid="quote-selected-property"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">
                    {selectedProp.address}, {selectedProp.city}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {ownerDisplayName(selectedProp)}
                    {selectedProp.zip_code ? ` · ${selectedProp.zip_code}` : ""}
                    {selectedProp.province ? ` (${selectedProp.province})` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    onPatchForm({ property_id: "" });
                    onSetPropSearch("");
                  }}
                  className="text-xs text-purple-700 hover:text-purple-900 hover:underline flex-shrink-0"
                  data-testid="quote-clear-property"
                >
                  Cambia
                </button>
              </div>
            )}
            {!selectedProp && (
              <>
                <div className="relative">
                  <Input
                    autoFocus
                    type="search"
                    value={propSearch}
                    onChange={(e) => onSetPropSearch(e.target.value)}
                    placeholder={`Cerca tra ${properties.length} immobili per indirizzo, città o proprietario...`}
                    className="pr-8"
                    data-testid="quote-select-property"
                  />
                  {propSearch && (
                    <button
                      type="button"
                      onClick={() => onSetPropSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      data-testid="quote-clear-search"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div
                  className="mt-2 max-h-64 overflow-y-auto border rounded-md divide-y bg-white"
                  data-testid="quote-property-list"
                >
                  {filtered.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      Nessun immobile trovato per "<strong>{propSearch}</strong>".
                      <div className="text-xs mt-1 text-purple-700">Torna indietro per crearne uno nuovo.</div>
                    </div>
                  ) : (
                    filtered.map((p) => (
                      <button
                        key={p._id}
                        type="button"
                        onClick={() => onPatchForm({ property_id: p._id })}
                        className="w-full text-left px-3 py-2 hover:bg-purple-50 transition-colors"
                        data-testid={`quote-prop-opt-${p._id}`}
                      >
                        <div className="text-sm font-medium truncate">
                          {p.address}, {p.city}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {ownerDisplayName(p)}
                          {p.zip_code ? ` · ${p.zip_code}` : ""}
                          {p.province ? ` (${p.province})` : ""}
                        </div>
                      </button>
                    ))
                  )}
                </div>
                {q && filtered.length > 0 && (
                  <div className="text-[11px] text-muted-foreground mt-1">
                    {filtered.length} risultat{filtered.length === 1 ? "o" : "i"} · continua a digitare per affinare
                  </div>
                )}
              </>
            )}
          </div>
          {properties.length === 0 && (
            <p className="text-xs text-amber-700 mt-2">
              Non hai ancora immobili. Torna indietro e scegli "Creazione manuale" o "Creazione con AI".
            </p>
          )}
          <div className="flex justify-start pt-1">
              <button
                type="button"
                onClick={goChoice}
                className="text-xs text-muted-foreground hover:text-foreground"
                data-testid="property-back-btn"
              >
                ← Indietro
              </button>
            </div>
          <Button
            className="w-full ht-gradient border-0"
            disabled={!form.property_id}
            onClick={onNext}
            data-testid="quote-property-next"
          >
            Avanti →
          </Button>
    </div>
  );
}
